import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { FaPencilAlt, FaCaretDown } from 'react-icons/fa';
import Swal from 'sweetalert2'; // SweetAlert2 for beautiful alerts

const ShippingEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State for form data (shipping type details)
  const [formData, setFormData] = useState({
    company: 'Royal mail',        // Default company value
    service: '',
    usuallyDeparts: '',
    outboundDays: null,           // Required but allows null temporarily
    inboundDays: null,            // Required
    price: null                   // Optional field
  });

  // UI state management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fetch existing shipping type data on component mount
  useEffect(() => {
    const fetchShipping = async () => {
      try {
        const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/shipping/${id}`);
        const data = await res.json();

        if (data.success) {
          // Populate form with fetched data, ensuring number fields are properly handled
          setFormData({
            ...data.data,
            outboundDays: data.data.outboundDays ?? null,
            inboundDays: data.data.inboundDays ?? null,
            price: data.data.price ?? null,
          });
        } else {
          // Show error if shipping type not found
          Swal.fire({
            icon: 'error',
            title: 'Not Found',
            text: 'Shipping type not found.',
            confirmButtonColor: '#004aad'
          });
          navigate('/case-setting');
        }
      } catch (err) {
        // Handle network or server errors
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load shipping type. Please try again later.',
          confirmButtonColor: '#004aad'
        });
        navigate('/case-setting');
      } finally {
        setLoading(false);
      }
    };

    fetchShipping();
  }, [id, navigate]);

  // Handle input changes - convert empty number inputs to null
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (['outboundDays', 'inboundDays', 'price'].includes(name)) {
      updatedValue = value === '' ? null : Number(value);
    }

    setFormData(prev => ({ ...prev, [name]: updatedValue }));
    setError(''); // Clear error when user starts typing
  };

  // Handle form save/update
  const handleSave = async () => {
    // Client-side validation: required fields
    if (
      !formData.service ||
      formData.outboundDays == null ||
      formData.inboundDays == null
    ) {
      setError('Please fill all required fields');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const body = {
        ...formData,
        service: formData.service.trim(),
        usuallyDeparts: formData.usuallyDeparts?.trim() || '',
      };

      const res = await fetch(`https://hms.backend.hiragroupcloud.com/api/shipping/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (data.success) {
        // Success message with SweetAlert2
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Shipping Type Updated Successfully!',
          confirmButtonColor: '#004aad'
        }).then(() => {
          navigate('/case-setting');
        });
      } else {
        setError(data.message || 'Error updating shipping type');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setSaving(false);
    }
  };

  // Disabled sidebar links (if used elsewhere)
  const disabledLinks = ["Store", "Permission Groups", "Finances"];
  const isDisabled = (name) => disabledLinks.includes(name);

  // Loading state UI
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div style={{ width: "95%", margin: "0 auto", padding: "0px" }}>
        <div className="row">
          {/* Main Content Area */}
          <div className="col-md-10">
            <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px' }}>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                Edit Shipping Type
              </h1>
              <h2 style={{ fontSize: '18px', color: '#004aad', margin: '0 0 10px 0', fontWeight: 'normal' }}>
                Shipping Type Details
              </h2>

              {/* Display validation or server errors */}
              {error && (
                <div style={{
                  color: '#721c24',
                  padding: '15px',
                  backgroundColor: '#f8d7da',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid #f5c6cb'
                }}>
                  {error}
                </div>
              )}

              {/* Form Container */}
              <div style={{
                backgroundColor: '#f4f4f4',
                padding: '30px',
                boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
                marginTop: '20px',
                borderTop: '4px solid #004aad',
                borderRadius: '8px'
              }}>
                {/* Company Selection */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Company <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <select
                      name="company"
                      value={formData.company || ''}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '10px 30px 10px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white',
                        appearance: 'none'
                      }}
                    >
                      <option>Royal mail</option>
                      <option>Uber</option>
                      <option>Local Driver</option>
                    </select>
                    <FaCaretDown style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#666',
                      fontSize: '16px'
                    }} />
                  </div>
                </div>

                {/* Service Name - Required */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Service <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="service"
                    value={formData.service || ''}
                    onChange={handleChange}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Usually Departs - Optional */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Usually Departs
                  </label>
                  <input
                    type="text"
                    name="usuallyDeparts"
                    value={formData.usuallyDeparts || ''}
                    onChange={handleChange}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                {/* Outbound Transit Days - Required */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Outbound Transit <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="outboundDays"
                    value={formData.outboundDays ?? ''}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    style={{
                      width: '80px',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#555' }}>Days</span>
                </div>

                {/* Inbound Transit Days - Required */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Inbound Transit Days <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="inboundDays"
                    value={formData.inboundDays ?? ''}
                    onChange={handleChange}
                    min="0"
                    step="1"
                    style={{
                      width: '80px',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#555' }}>Days</span>
                </div>

                {/* Price - Optional */}
                <div style={{ marginBottom: '30px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontWeight: 'bold', color: '#333', width: '140px', flexShrink: 0 }}>
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price ?? ''}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    style={{
                      width: '100px',
                      padding: '10px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#555' }}>£ (optional)</span>
                </div>

                {/* Update Button */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    style={{
                      backgroundColor: '#004aad',
                      color: 'white',
                      padding: '12px 30px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      fontWeight: 'bold',
                      opacity: saving ? 0.7 : 1,
                      transition: 'background-color 0.3s'
                    }}
                  >
                    <FaPencilAlt />
                    {saving ? 'Saving...' : 'Update'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShippingEdit;